import React, { useRef, useState } from 'react';
import { tr } from '../../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { AppButton } from '../../index';
import AppDataTable, { ColumnConfig } from '../../DataTable/AppDataTable';
import { ModalHandle } from '../../Modals/primaryModal';
import StatusTag from '../../labels/statusLabel';
import { useAppDispatch, useAppSelector } from '../../../redux/stateHooks';
import { fetchMessageQueueJobs } from '../../../redux/action/messageQueue.action';
import { showNotification } from '../../../redux/slices/notification.slice';
import type { IJobRecord } from '../../../interface/message-queue.interface';
import { PrimaryModal } from '../../Modals';
import UploadSummary from '../upload-summary/UploadSummary';
import { getCurrentFormattedTime } from '../../../utility/utility';

interface UploadHistoryModalProps {
  /**
   * The job name/type to filter by (e.g., 'STUDENT_BULK_UPLOAD', 'CURRICULUM_BULK_UPLOAD')
   */
  jobName: string;

  /**
   * Title displayed in modal header
   */
  title: string;
}

/**
 * Reusable Upload History Modal Component
 *
 * Displays job history for bulk upload operations with status tracking,
 * progress monitoring, and result file downloads.
 *
 * @example
 * ```tsx
 * const modalRef = useRef<ModalHandle>(null);
 *
 * <AppButton
 *   label={tr('fe_bulk_upload.view_history', "View History")}
 *   onClick={() => modalRef.current?.openModal()}
 * />
 *
 * <UploadHistoryModal
 *   ref={modalRef}
 *   jobName="STUDENT_BULK_UPLOAD"
 *   title={t('dialog.student_upload_history', "Student Upload History")}
 * />
 * ```
 */
const UploadHistoryModal = React.forwardRef<ModalHandle, UploadHistoryModalProps>(
  ({ jobName, title }, ref) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { jobs = [], isLoading, totalRecords } = useAppSelector((state) => state.messageQueue);
    const internalRef = useRef<ModalHandle>(null);
    const summaryModalRef = useRef<ModalHandle>(null);
    const [selectedJob, setSelectedJob] = useState<IJobRecord | null>(null);

    // Combine external and internal refs
    React.useImperativeHandle(ref, () => ({
      openModal: () => {
        internalRef.current?.openModal();
        loadJobs();
      },
      closeModal: () => {
        internalRef.current?.closeModal();
      },
    }));

    /**
     * Load jobs by dispatching Redux action
     */
    const loadJobs = async () => {
      try {
        await dispatch(fetchMessageQueueJobs({ jobName })).unwrap();
      } catch (err: any) {
        dispatch(
          showNotification({
            severity: 'error',
            message:
              err?.message ||
              tr('g.could_not_retrieve_job_history', 'Could not retrieve job history.'),
          }),
        );
      }
    };

    /**
     * Status badge template with color coding
     */
    const statusBodyTemplate = (rowData: IJobRecord) => {
      return <StatusTag status={rowData.status} />;
    };

    /**
     * Date formatting template
     */
    const dateBodyTemplate = (rowData: IJobRecord) => {
      return (
        <span className="text-sm font-poppins">
          {getCurrentFormattedTime(rowData.created_at, 'DD MMM YYYY, h:mm A')}
        </span>
      );
    };

    /**
     * Progress tracking template
     */
    const progressBodyTemplate = (rowData: IJobRecord) => {
      const successCount = rowData.result?.successCount || 0;
      const failedCount = rowData.result?.failureCount || 0;
      const totalCount = successCount + failedCount;

      return (
        <span className="text-sm font-poppins">
          {successCount} / {totalCount}
          {failedCount > 0 && (
            <span className="text-red-600 ml-2">
              {tr('bulk_upload.errors_count_paren', '({{count}} errors)', { count: failedCount })}
            </span>
          )}
        </span>
      );
    };

    /**
     * Actions template with download link
     */
    const actionsBodyTemplate = (rowData: IJobRecord) => {
      if (!rowData.meta_data?.Location)
        return <span className="text-gray-400">{t('common.na', 'N/A')}</span>;

      return (
        <a
          href={rowData.meta_data.Location}
          download
          className="text-sm font-poppins text-project-purple underline hover:no-underline transition-all"
        >
          {t('g.download', 'Download')}
        </a>
      );
    };

    /**
     * View summary action button
     */
    const viewSummaryBodyTemplate = (rowData: IJobRecord) => {
      return (
        <AppButton
          icon="pi pi-eye"
          onClick={() => handleViewSummary(rowData)}
          text
          raised
          tooltip={t('g.view_summary', 'View Summary')}
        />
      );
    };

    /**
     * Handle view summary action
     */
    const handleViewSummary = (job: IJobRecord) => {
      setSelectedJob(job);
      summaryModalRef.current?.openModal();
    };

    /**
     * Table column configuration
     */
    const tableColumns: ColumnConfig<IJobRecord>[] = [
      {
        header: t('upload_history.column_job_id', 'Job ID'),
        field: 'id',
        bodyStyle: { width: '120px' },
      },
      {
        header: t('common.column_status', 'Status'),
        body: statusBodyTemplate,
        bodyStyle: { width: '140px' },
      },
      {
        header: t('common.column_created_at', 'Created At'),
        body: dateBodyTemplate,
        bodyStyle: { width: '180px' },
      },
      {
        header: t('upload_history.column_progress', 'Progress'),
        body: progressBodyTemplate,
        bodyStyle: { width: '150px' },
      },
      {
        header: t('upload_history.column_download_results', 'Download Results'),
        body: actionsBodyTemplate,
        bodyStyle: { width: '150px' },
      },
      {
        header: t('common.column_actions', 'Actions'),
        body: viewSummaryBodyTemplate,
        bodyStyle: { width: '100px', textAlign: 'center' },
      },
    ];

    /**
     * Handle modal close
     */
    const handleClose = () => {
      internalRef.current?.closeModal();
    };

    const handleSummaryModalClose = () => {
      setSelectedJob(null);
      summaryModalRef.current?.closeModal();
    };
    /**
     * Modal content
     */
    const modalContent = (
      <>
        <AppDataTable
          data={jobs}
          columns={tableColumns}
          loading={isLoading}
          totalRecords={totalRecords}
          showPaginator={jobs.length > 5}
          pageSize={5}
          emptyMessage={t(
            'upload_history.empty_no_jobs',
            'No upload jobs found for this configuration.',
          )}
        />

        <div className="flex flex-col md:flex-row justify-end gap-2 mt-4 pt-4 border-t">
          <div className="w-full md:w-1/4">
            <AppButton label={t('common.refresh', 'Refresh')} onClick={loadJobs} outlined />
          </div>
          <div className="w-full md:w-1/4">
            <AppButton
              label={t('common.button_close')}
              onClick={() => internalRef.current?.closeModal()}
            />
          </div>
        </div>
      </>
    );

    return (
      <>
        <PrimaryModal
          ref={internalRef}
          title={title}
          content={modalContent}
          width="70vw"
          showHeader
          onClose={handleClose}
        />

        <PrimaryModal
          ref={summaryModalRef}
          title={t('dialog.upload_summary', 'Upload Summary')}
          content={<UploadSummary job={selectedJob} />}
          width="50vw"
          showHeader={!!selectedJob}
          onClose={handleSummaryModalClose}
        />
      </>
    );
  },
);

UploadHistoryModal.displayName = 'UploadHistoryModal';

export default UploadHistoryModal;
